import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { type RootState } from '@/platform/store/types';
import {
  type GoogleCalendarConnectedData,
  type GoogleCalendarErrorScope,
  type GoogleCalendarIntegrationStatus,
} from '../models/google-calendar-integration.model';
import type { WithSlice } from '@reduxjs/toolkit';

export interface GoogleCalendarIntegrationError {
  step: GoogleCalendarErrorScope;
  detail: string;
}

export interface GoogleCalendarIntegrationState {
  status: GoogleCalendarIntegrationStatus;
  data: GoogleCalendarConnectedData | null;
  error: GoogleCalendarIntegrationError | null;
}

const initialState: GoogleCalendarIntegrationState = {
  status: 'idle',
  data: null,
  error: null,
};

export const googleCalendarIntegrationSlice = createSlice({
  name: 'googleCalendarIntegration',
  initialState,
  reducers: {
    setStatusLoading: (state) => {
      state.status = 'loading';
      state.error = null;
    },
    setStatusConnecting: (state) => {
      state.status = 'connecting';
      state.error = null;
    },
    setConnected: (
      state,
      action: PayloadAction<GoogleCalendarConnectedData>,
    ) => {
      state.status = 'connected';
      state.data = action.payload;
      state.error = null;
    },
    setDisconnected: (state) => {
      state.status = 'disconnected';
      state.data = null;
      state.error = null;
    },
    setError: (
      state,
      action: PayloadAction<GoogleCalendarIntegrationError>,
    ) => {
      state.status = 'error';
      state.error = action.payload;
    },
    resetIntegrationState: () => initialState,
  },
});

export const {
  setStatusLoading,
  setStatusConnecting,
  setConnected,
  setDisconnected,
  setError,
  resetIntegrationState,
} = googleCalendarIntegrationSlice.actions;

const selectSlice = (state: RootState): GoogleCalendarIntegrationState =>
  state.googleCalendarIntegration ?? initialState;

export const selectGoogleCalendarStatus = (state: RootState) =>
  selectSlice(state).status;

export const selectGoogleCalendarData = (state: RootState) =>
  selectSlice(state).data;

export const selectGoogleCalendarError = (state: RootState) =>
  selectSlice(state).error;

// Registers this slice in the typed root state without the platform importing it.
declare module '@/platform/store/rootReducer' {
  interface LazyLoadedSlices extends WithSlice<typeof googleCalendarIntegrationSlice> {}
}
